import React, { useEffect, useState } from 'react';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import AdVariantCard from './AdVariantCard';
import AdVariantSkeleton from './AdVariantSkeleton';
import { AdTemplateVariant } from './adTemplateIdeas';

// Grid behaviour ported from whatsapp-console-frontend
// `src/pages/AdTemplates/TemplateGrid.js`: same columns, and the expanded card
// grows to two columns with its details beside the preview.

const GAP = 16;

export type GridCell =
  | { key: string; kind: 'variant'; variant: AdTemplateVariant }
  | { key: string; kind: 'skeleton'; progress?: { stage?: string; percent?: number } | null };

const useStyles = makeStyles(() => ({
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: GAP,
  },
  cell: {
    minWidth: 0,
    display: 'flex',
    '& > *': { width: '100%' },
  },
  cellExpanded: {
    transition: 'width 350ms ease',
  },
}));

const AdVariantGrid = (props: Props): React.ReactElement => {
  const { cells } = props;
  const classes = useStyles();
  const theme = useTheme();
  const below1400 = useMediaQuery(theme.breakpoints.down(1400));
  const below1050 = useMediaQuery(theme.breakpoints.down(1050));
  const below700 = useMediaQuery(theme.breakpoints.down(700));
  const columns = below700 ? 1 : below1050 ? 2 : below1400 ? 3 : 4;
  const cellWidth = `calc((100% - ${GAP * (columns - 1)}px) / ${columns})`;
  const expandedWidth =
    columns === 1
      ? '100%'
      : `calc((100% - ${GAP * (columns - 1)}px) / ${columns} * 2 + ${GAP}px)`;

  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [gridNode, setGridNode] = useState<HTMLDivElement | null>(null);
  const [gridWidth, setGridWidth] = useState(0);

  useEffect(() => {
    if (!gridNode) {
      return undefined;
    }
    const measure = () => setGridWidth(gridNode.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(gridNode);
    return () => observer.disconnect();
  }, [gridNode]);

  const collapsedWidth = gridWidth
    ? Math.floor((gridWidth - GAP * (columns - 1)) / columns)
    : 0;
  const detailsWidth = columns >= 2 && collapsedWidth ? collapsedWidth + GAP : 0;

  // An expanded card in the last column takes the row's first cell's place,
  // so that cell is hidden while it is open (same as the console).
  const expandedIndex = cells.findIndex((cell) => cell.key === expandedKey);
  const hiddenIndex = (() => {
    if (expandedIndex < 0 || columns < 2) {
      return -1;
    }
    const posInRow = expandedIndex % columns;
    if (posInRow !== columns - 1) {
      return -1;
    }
    return expandedIndex - posInRow;
  })();

  return (
    <div className={classes.grid} ref={setGridNode}>
      {cells.map((cell, index) =>
        index === hiddenIndex ? null : (
          <div
            key={cell.key}
            className={`${classes.cell} ${expandedKey === cell.key ? classes.cellExpanded : ''}`}
            style={{
              width: expandedKey === cell.key ? expandedWidth : cellWidth,
              ...(cell.kind === 'skeleton' ? { alignSelf: 'stretch' } : {}),
            }}
          >
            {cell.kind === 'skeleton' ? (
              <AdVariantSkeleton progress={cell.progress} />
            ) : (
              <AdVariantCard
                variant={cell.variant}
                expanded={expandedKey === cell.key}
                previewWidth={collapsedWidth}
                detailsWidth={detailsWidth}
                onToggleExpand={() =>
                  setExpandedKey((current) => (current === cell.key ? null : cell.key))
                }
              />
            )}
          </div>
        ),
      )}
    </div>
  );
};

interface Props {
  cells: GridCell[];
}

export default AdVariantGrid;
